import { BRAND } from './brand';
import { utmCta } from './shared-ctas';
import type { PageCopy } from './page-copy-types';
import { SPARK_PRICE } from '@/lib/pricing/plans';

// ─── /ai-receptionist (SEO/AEO pillar) ──────────────────────────────────
// Category-defining page for the "AI receptionist" query family.
// Sits above /simple-setup, /ai-business-page, and /ai-voice in the
// content hierarchy. Targets informational + commercial intent queries.
// NOTE: 301-redirects to /ai-voice-receptionist (consolidated canonical).
export const AI_RECEPTIONIST_COPY: PageCopy = {
  eyebrow: 'AI Receptionist',
  h1: 'Your AI Receptionist',
  lede: 'Hear the difference. Talk to Bri right now — she answers calls, captures leads, and books appointments 24/7.',
  ctaPrimary: utmCta({
    label: 'Get started',
    source: 'organic',
    medium: 'landing',
    campaign: 'ai-receptionist',
  }),
  // canonical-divergence: managed/enterprise setup offer routes to the
  // Enterprise contact path (not the hidden plan selector).
  ctaSecondary: { label: 'Get Custom Setup', href: '/enterprise-pricing' },
  trustSignals: [`${SPARK_PRICE} AI Receptionist`, '30-day money-back guarantee', 'Cancel anytime', 'Custom setup available'],
  sections: [
    {
      id: 'what-is',
      eyebrow: 'Definition',
      heading: 'What Is an AI Receptionist?',
      body: 'An AI receptionist is a voice AI that answers your business phone, qualifies callers, books appointments, and sends follow-up texts — automatically, without human involvement. Unlike a human receptionist or answering service, an AI receptionist works 24/7 at a fraction of the cost.',
    },
    {
      id: 'problem',
      heading: 'Small Businesses Lose $1,000s to Missed Calls Every Month',
      body: 'When your phone goes to voicemail, 80% of callers hang up and call someone else. After hours, the problem is worse — and that gap is where revenue evaporates.',
      bullets: [
        'Missed call = missed lead. Every unanswered ring is revenue walking to a competitor.',
        'After-hours gap. Most small businesses close at 5 PM, but 40% of calls come after hours.',
        'Staffing cost. A full-time receptionist costs $2,500–$4,000/month before benefits.',
      ],
    },
    {
      id: 'how-it-works',
      eyebrow: 'How it works',
      heading: "How BizRnR's AI Receptionist Works",
      body: `Forward your business phone number to BizRnR in under ${BRAND.setupTimeClaim}. Bri, our AI voice agent, answers every call immediately, collects caller information, and either books an appointment or sends a follow-up SMS. You get a summary of every interaction in real-time.`,
    },
    {
      id: 'comparison',
      heading: "AI Receptionist vs Human Receptionist: What's the Difference?",
      body: "An AI receptionist provides 24/7 coverage at a fraction of the cost of a human receptionist or answering service — with faster response times, zero missed calls, and automatic SMS follow-up included.",
    },
    {
      id: 'industries',
      heading: 'AI Receptionist for Every Small Business',
      body: "BizRnR's AI receptionist is trained for 20+ small business verticals. Whether you run an HVAC company, dental practice, real estate agency, or restaurant, Bri knows your industry's terminology and how to handle your specific caller needs.",
    },
    {
      id: 'pricing',
      heading: 'How Much Does an AI Receptionist Cost?',
      body: "The AI Receptionist plan ($99/month, 100 interactions) is available for self-serve setup with everything you need to answer, qualify, and book callers automatically. Higher-volume teams that need outbound campaigns, multi-location, or custom workflows can talk to us about Enterprise (custom pricing) and optional managed setup by our team.",
    },
    {
      id: 'faq',
      heading: 'Frequently Asked Questions About AI Receptionists',
    },
    {
      id: 'final-cta',
      heading: 'Your AI Receptionist. Ready When You Are.',
      body: 'Start AI Receptionist for $99/mo. Or let our team configure everything — custom scripts, your hours, your industry. No technical work on your end.',
      reassurance: 'No long-term contracts. Cancel anytime.',
    },
  ],
  faqs: [
    {
      q: 'What is an AI receptionist?',
      a: 'An AI receptionist is software that uses voice AI and natural language processing to answer business phone calls, qualify leads, and handle customer inquiries automatically. Unlike a human receptionist, it works 24/7 without breaks or overtime pay.',
    },
    {
      q: 'How does an AI receptionist work?',
      a: "You forward your business phone number to the AI. When a call comes in, the AI answers immediately, introduces itself as your business's receptionist, asks the right questions, collects information, and either books an appointment or sends an SMS follow-up. The whole interaction is logged in your dashboard.",
    },
    {
      q: 'Can an AI receptionist book appointments?',
      a: "Yes. BizRnR's AI receptionist can book appointments directly into your calendar system, send confirmation SMS messages to callers, and send you a real-time notification for every booked appointment.",
    },
    {
      q: 'How much does an AI receptionist cost?',
      a: "BizRnR's AI receptionist starts at $99/month with cancel-anytime billing and a 30-day money-back guarantee. This compares to $2,500–$4,000/month for a full-time human receptionist or $1.50–$2.00 per minute for traditional answering services.",
    },
    {
      q: "What's the difference between an AI receptionist and an answering service?",
      a: "An answering service uses human agents who answer calls on your behalf. An AI receptionist uses voice AI to handle calls automatically — with faster response times, 24/7 availability, and dramatically lower cost. BizRnR also includes SMS follow-up and CRM integration that most answering services don't offer.",
    },
    {
      q: 'Which businesses benefit most from an AI receptionist?',
      a: "Any business that receives phone calls benefits — especially those that miss after-hours calls or can't always answer during business hours. Common industries include HVAC, dental practices, real estate, auto repair, restaurants, legal services, and property management.",
    },
    {
      q: 'How long does it take to set up an AI receptionist?',
      a: `Self-serve setup takes under ${BRAND.setupTimeClaim} — forward your number and your AI receptionist answers immediately. If you choose our managed setup service, our team configures everything specifically for your business — no technical knowledge required on your end.`,
    },
    {
      q: 'Do I need any technical knowledge to get started?',
      a: 'Not at all. The self-serve path has a simple setup wizard — most business owners complete it in minutes. If you prefer our team to handle everything, our managed setup service covers phone integration, custom scripts, business hours, and industry configuration. You don\'t need to touch a thing.',
    },
  ],
  metadata: {
    title: 'AI Receptionist for Small Business | BizRnR',
    description:
      "BizRnR's AI receptionist answers every call 24/7, books appointments, and sends SMS follow-ups. Works for 20+ industries. $99/mo with a 30-day money-back guarantee.",
    canonical: 'https://bizrnr.com/ai-receptionist',
  },
};
